// Library Methods
import { useCallback, useEffect, useRef, useState } from "react";
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";

// Services
import { getPortalHub, subscribeToClinic } from "src/services/signalR/api";

// Utils
import { isProduction } from "src/utils/env/envHelper";
import { webServiceUrl } from "src/utils/services/servicesHelper";

// Hooks
import useTokenAccess from "../auth/useTokenAccess";

// Delay hiding the loading state after coming back online so it doesn't flicker off
// before the reconnect's resubscribe (onSignalRConnected) has actually settled.
const RECONNECT_LOADING_DELAY = 2000;

type UseOnlineConnectionType = {
  clinicId: string;
  handleDeviceListUpdated: (data: string) => void;
};

const useOnlineConnection = ({
  clinicId,
  handleDeviceListUpdated,
}: UseOnlineConnectionType) => {
  const { getToken } = useTokenAccess();
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isOnline, setIsOnline] = useState(false);
  const [isLoadingSignalR, setIsLoadingSignalR] = useState(false);

  // Keep latest prop/callback values in refs so they can be read inside stable
  // callbacks without those callbacks needing to be recreated on every change.
  // Assigned during render (not in an effect) so values are always current by the
  // time any async callback fires.
  const clinicIdRef = useRef(clinicId);
  const getTokenRef = useRef(getToken);
  const handleDeviceListUpdatedRef = useRef(handleDeviceListUpdated);
  clinicIdRef.current = clinicId;
  getTokenRef.current = getToken;
  handleDeviceListUpdatedRef.current = handleDeviceListUpdated;

  // Stable reference — reads live values via refs so this callback never changes
  // and never triggers the connection effect to re-run.
  const onSignalRConnected = useCallback(async (conn: HubConnection) => {
    if (conn && conn.state === HubConnectionState.Connected) {
      try {
        const token = await getTokenRef.current();
        const response = await subscribeToClinic(
          token,
          clinicIdRef.current,
          conn
        );
        const data = JSON.parse(response);
        if (data?.state === true) {
          conn.off("DeviceListUpdated");
          conn.on("DeviceListUpdated", function (data) {
            handleDeviceListUpdatedRef.current(data);
          });
        }
      } catch (e) {
        // console.log("Could not subscribe to clinic.");
      }
    }
  }, []); // intentionally empty — all deps accessed through refs

  // Build the connection once on mount.
  useEffect(() => {
    const signalRConnection = async () => {
      try {
        const hubName = await getPortalHub();
        const newConnection = new HubConnectionBuilder()
          .configureLogging(isProduction ? LogLevel.None : LogLevel.Debug)
          .withUrl(`${webServiceUrl}/${hubName?.properties?.hub}`, {
            transport: HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect({
            // Retry quickly for brief drops (e.g. a VR headset losing Wi-Fi for a
            // moment) and only back off once the outage looks sustained.
            nextRetryDelayInMilliseconds: (retryContext) => {
              if (retryContext.elapsedMilliseconds < 10000) {
                return Math.random() * 1000;
              }
              return (Math.random() + 0.2) * 10000;
            },
          })
          .build();

        setConnection(newConnection);
      } catch (e) {
        // console.log(e);
      }
    };
    signalRConnection();
  }, []);

  // Start the connection and register event handlers. Because onSignalRConnected
  // is stable and `connection` only changes once (when first created), this effect
  // runs exactly once — so handlers are registered exactly once and the cleanup
  // (stop) only fires on unmount, not on every render.
  useEffect(() => {
    if (!connection || connection.state !== HubConnectionState.Disconnected)
      return;

    let isMounted = true;

    connection
      .start()
      .then(() => {
        if (!isMounted) return;
        console.log("Connected!");
        setIsOnline(true);
        onSignalRConnected(connection);

        // Fires as soon as the transport drops — a much faster disconnect signal
        // than polling, since it's driven by the actual socket error/timeout.
        connection.onreconnecting(() => {
          setIsOnline(false);
        });

        connection.onreconnected(async (connectionId) => {
          console.log(
            `Reconnected. Connection Id: ${connectionId} or ${connection.connectionId}`
          );
          await onSignalRConnected(connection);
          setIsOnline(true);
        });
      })
      .catch((e) => {
        if (!isMounted) return; // stop() called during negotiation on unmount — expected
        console.error("Connection failed: ", e);
      });

    connection.onclose((e) => {
      if (!isMounted) return;
      setIsOnline(false);
      if (e) {
        console.error(`Connection closed with error: ${e}`);

        if (connection.state === HubConnectionState.Disconnected) {
          connection
            .start()
            .then(() => {
              onSignalRConnected(connection);
              setIsOnline(true);
            })
            .catch((e) => {
              console.error("Connection start failed after disconnect: ", e);
            });
        }
      } else {
        console.warn("Connection closed.");
      }
    });

    return () => {
      isMounted = false;
      if (connection) {
        console.warn("Connection will stop.");
        connection.stop();
      }
    };
  }, [connection, onSignalRConnected]);

  // Debounce hiding the loading state so it doesn't flicker off before the
  // reconnect's resubscribe has actually settled; show it immediately when offline.
  useEffect(() => {
    if (!isOnline) {
      setIsLoadingSignalR(true);
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsLoadingSignalR(false);
    }, RECONNECT_LOADING_DELAY);
    return () => clearTimeout(timeoutId);
  }, [isOnline]);

  return { connection, isOnline, isLoadingSignalR };
};

export default useOnlineConnection;
